import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { sendContactMessage, resetContactState } from "../../../../redux/slices/contactUsSlice";
import { logoutMaster } from "../../../../redux/slices/masterSessionSlice";
import { logoutUserSession } from "../../../../redux/slices/userSessionSlice";
import { resetStore } from "../../../../redux/slices/resetStore";

const CustomAlert = lazy(() => import("../../../../Components/CustomAlert/CustomAlert"));

/* ── Поменяй на свои данные ── */
const WHATSAPP_NUMBER = "972509718887"; // без + и пробелов
const SITE_ORIGIN = "https://zmanup.co.il";
const CONTACTS_PATH = "/contacts";
const PRIVACY_PATH = "/privacy";

/* ── Переводы ── */
const TR = {
    support_section: { ru: "Поддержка", he: "תמיכה", en: "Support" },
    support_title: { ru: "Связаться с нами", he: "צור קשר", en: "Contact us" },
    support_sub: { ru: "Ответим в течение нескольких минут", he: "נענה תוך מספר דקות", en: "We reply within minutes" },
    wa_btn: { ru: "Написать в WhatsApp", he: "כתוב בWhatsApp", en: "Write on WhatsApp" },
    contact_page: { ru: "Страница поддержки", he: "עמוד תמיכה", en: "Support page" },
    idea_title: { ru: "Есть идея как улучшить наш сервис?", he: "יש לך רעיון איך לשפר את השירות שלנו?", en: "Have an idea how to improve our service?" },
    idea_sub: { ru: "Мы всегда рады новым предложениям!", he: "אנחנו תמיד שמחים לרעיונות חדשים!", en: "We love new suggestions!" },
    idea_ph: { ru: "Напишите вашу идею по улучшению...", he: "כתוב את הרעיון שלך...", en: "Write your idea here..." },
    idea_send: { ru: "Отправить идею", he: "שלח רעיון", en: "Send idea" },
    idea_sending: { ru: "Отправляем...", he: "שולח...", en: "Sending..." },
    idea_sent: { ru: "Спасибо! Мы обязательно рассмотрим ✓", he: "תודה! נשקול את הרעיון ✓", en: "Thanks! We'll review it ✓" },
    idea_error: { ru: "Ошибка отправки. Попробуйте ещё раз", he: "שגיאה. נסה שוב", en: "Error. Try again" },
    other: { ru: "Прочее", he: "אחר", en: "Other" },
    privacy: { ru: "Конфиденциальность", he: "פרטיות", en: "Privacy" },
    logout: { ru: "Выйти", he: "התנתק", en: "Log out" },
    logout_confirm: { ru: "Вы уверены?", he: "האם אתה בטוח?", en: "Are you sure?" },
    logout_msg: { ru: "Вы действительно хотите выйти?", he: "האם אתה רוצה להתנתק?", en: "Do you really want to log out?" },
    cancel: { ru: "Отмена", he: "ביטול", en: "Cancel" },
};

const tr = (key, lang) => TR[key]?.[lang] || TR[key]?.ru || key;

export default function SettingsPanel() {
    const { i18n } = useTranslation();
    const lang = i18n.language?.slice(0, 2) || "ru";
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const client = useSelector(s => s.userSession?.currentAccount);
    const status = useSelector(s => s.contactUs?.status);
    const error = useSelector(s => s.contactUs?.error);

    const [idea, setIdea] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);

    useEffect(() => () => dispatch(resetContactState()), [dispatch]);

    const waText = encodeURIComponent(tr("support_sub", lang));
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${waText}`;

    const handleSendIdea = () => {
        if (!idea.trim() || status === "loading") return;
        dispatch(sendContactMessage({
            name: `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "Клиент",
            email: client?.email || "no-email@zmanup.co.il",
            phone: client?.phone || "",
            subject: "💡 Идея по улучшению приложения",
            message: idea.trim(),
        }));
    };

    // Та же логика что в AppHeader
    const handleConfirmLogout = () => {
        // ← добавить эти две строки:
        localStorage.removeItem("masterUid");
        localStorage.removeItem("lastVisitedMasterLink");

        localStorage.removeItem("token");
        localStorage.removeItem("userType");
        localStorage.removeItem("uid");
        delete axios.defaults.headers.common.Authorization;
        dispatch(resetStore());
        dispatch(logoutUserSession());
        dispatch(logoutMaster());
        try { sessionStorage.removeItem('master_account'); } catch { }
        navigate(`/${lang}`);
        setAlertOpen(false);
    };

    const isSent = status === "succeeded";
    const isSending = status === "loading";
    const isError = status === "failed";

    return (
        <>
            <div className="cd-content">

                {/* ── Поддержка ── */}
                <p className="cd-sec-title">{tr("support_section", lang)}</p>

                <div className="cd-support-card">
                    <div className="cd-support-card__head">
                        <div className="cd-support-card__icon">💬</div>
                        <div>
                            <div className="cd-support-card__title">{tr("support_title", lang)}</div>
                            <div className="cd-support-card__sub">{tr("support_sub", lang)}</div>
                        </div>
                    </div>

                    <a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cd-support-card__btn cd-support-card__btn--wa"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        {tr("wa_btn", lang)}
                    </a>

                    <button
                        className="cd-support-card__btn cd-support-card__btn--page"
                        onClick={() => window.open(`${SITE_ORIGIN}/${lang}${CONTACTS_PATH}`, "_blank", "noopener,noreferrer")}
                    >
                        🌐 {tr("contact_page", lang)}
                    </button>
                </div>

                {/* ── Идеи ── */}
                <div className="cd-idea-card">
                    <div className="cd-idea-card__head">
                        <span className="cd-idea-card__emoji">💡</span>
                        <div>
                            <div className="cd-idea-card__title">{tr("idea_title", lang)}</div>
                            <div className="cd-idea-card__sub">{tr("idea_sub", lang)}</div>
                        </div>
                    </div>

                    {isSent ? (
                        <div className="cd-idea-card__sent">{tr("idea_sent", lang)}</div>
                    ) : (
                        <>
                            <textarea
                                className="cd-idea-card__textarea"
                                placeholder={tr("idea_ph", lang)}
                                value={idea}
                                onChange={e => setIdea(e.target.value)}
                                rows={3}
                                disabled={isSending}
                            />
                            {isError && (
                                <div className="cd-idea-card__error">{error || tr("idea_error", lang)}</div>
                            )}
                            <button
                                className="cd-idea-card__btn"
                                onClick={handleSendIdea}
                                disabled={!idea.trim() || isSending}
                            >
                                {isSending ? tr("idea_sending", lang) : `✓ ${tr("idea_send", lang)}`}
                            </button>
                        </>
                    )}
                </div>

                {/* ── Прочее ── */}
                <p className="cd-sec-title" style={{ marginTop: 8 }}>{tr("other", lang)}</p>
                <div className="cd-card">
                    <button className="cd-srow" onClick={() => window.open(`${SITE_ORIGIN}/${lang}${PRIVACY_PATH}`, "_blank", "noopener,noreferrer")}>
                        <span className="cd-srow__ico">🔒</span>
                        <span className="cd-srow__lbl">{tr("privacy", lang)}</span>
                        <span className="cd-srow__chev">›</span>
                    </button>
                    <button className="cd-srow cd-srow--danger" onClick={() => setAlertOpen(true)}>
                        <span className="cd-srow__ico cd-srow__ico--danger">🚪</span>
                        <span className="cd-srow__lbl">{tr("logout", lang)}</span>
                    </button>
                </div>

            </div>

            {/* ── Confirm logout ── */}
            <Suspense fallback={null}>
                <CustomAlert
                    isOpen={alertOpen}
                    title={tr("logout_confirm", lang)}
                    message={tr("logout_msg", lang)}
                    cancelText={tr("cancel", lang)}
                    confirmText={tr("logout", lang)}
                    onCancel={() => setAlertOpen(false)}
                    onConfirm={handleConfirmLogout}
                />
            </Suspense>
        </>
    );
}